import React, { useCallback, useEffect } from "react";

import dayjs from "dayjs";
import { isEmpty } from "lodash";
import { useNavigate, useParams } from "react-router-dom";
import { FilterContext, useFilter } from "../../components/Filter";
import {
    ListView,
    MainPage,
    MainView,
    PageContext,
    usePageState,
} from "../../components/PageLayout";
import { initPeriod } from "../../components/Period";
import { useSideSheet } from "../../components/SideSheet";
import { useUserInfo } from "../../utils/security";
import { fetchEstimateDetail, fetchEstimateList, fetchEstimateOverview } from "./mock";
import { EstimatesList } from "./EstimatesList";
import { EstimatesOverview } from "./EstimatesOverview";
import { EstimatesDetail } from "./EstimatesDetail";

export const EstimatesPage = ({ pagePath }) => {
    const navigate = useNavigate();
    const { id: detailId } = useParams();
    const sideSheet = useSideSheet();
    const { userInfo } = useUserInfo();

    const filterState = useFilter({
        filter: {
            period: initPeriod("year"),
        },
    });
    const { filterValue } = filterState;

    const pageState = usePageState();
    const { state, setListLoading, setList, setDataLoading, setData, isFull } = pageState;
    const isDetailIdSet = !isEmpty(detailId);
    const showList = isFull || !isDetailIdSet;
    const showMain = isFull || isDetailIdSet;

    //TODO - implement functions TODO
    const addEstimate = useCallback(
        ({ id, name, ...rest }) => {
            sideSheet.show({
                employeeTypeId: id,
                employeeTypeName: name,
                date: dayjs(),
                fiscalYear: dayjs().year(),
                dueDate:
                    rest.hasDueDate && rest.defaultDueDateDays
                        ? dayjs().add(rest.defaultDueDateDays || 0, "days")
                        : undefined,
                currencyCode: userInfo?.defaultCurrency?.code || "CZK",
                ...(rest.hasSubjectInfo
                    ? {
                          subjectName: userInfo?.financesSettings?.name,
                          subjectAddress: userInfo?.financesSettings?.address,
                          subjectIdNumber: userInfo?.financesSettings?.idNumber,
                          subjectTaxIdNumber: userInfo?.financesSettings?.taxIdNumber,
                      }
                    : {}),
                ...rest,
            });
        },
        [sideSheet, userInfo]
    );

    useEffect(() => {
        const fetchList = async (filter) => {
            setListLoading(true);
            try {
                const data = await fetchEstimateList(filter);
                setList(data);
            } catch (e) {
                console.error(e);
                setList({ error: true });
            } finally {
                setListLoading(false);
            }
        };

        const fetchOverview = async (filter) => {
            setDataLoading(true);
            try {
                const data = await fetchEstimateOverview(filter);
                setData(data);
            } catch (e) {
                console.error(e);
                setList({ error: true });
            } finally {
                setDataLoading(false);
            }
        };

        fetchList(filterValue);
        fetchOverview(filterValue);
    }, [filterValue]);

    //fetch loaded data
    useEffect(() => {
        const fetchDetail = async (id) => {
            console.log("fetching detail", id);
            setDataLoading(true);
            try {
                const data = await fetchEstimateDetail(id);
                setData(data);
            } catch (e) {
                console.error(e);
            } finally {
                setDataLoading(false);
            }
        };
        if (!isEmpty(detailId)) {
            fetchDetail(detailId);
        }
    }, [detailId, setData, fetchEstimateDetail]);

    return (
        <>
            <FilterContext.Provider value={filterState}>
                <PageContext.Provider value={pageState}>
                    <MainPage>
                        {showList && (
                            <ListView>
                                <EstimatesList
                                    onAddEstimate={addEstimate}
                                    selectItem={({ id }) => navigate(`/${pagePath}/${id}`)}
                                    loading={state.listLoading}
                                    data={state.list}
                                    detailId={detailId}
                                />
                            </ListView>
                        )}
                        {showMain && (
                            <MainView>
                                {isDetailIdSet ? (
                                    <EstimatesDetail
                                        employeeId={detailId}
                                        onBack={() => navigate(`/${pagePath}`)}
                                        loading={state.dataLoading}
                                        data={state.data}
                                    />
                                ) : (
                                    <EstimatesOverview />
                                )}
                            </MainView>
                        )}
                    </MainPage>
                </PageContext.Provider>
            </FilterContext.Provider>
        </>
    );
};
